<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FlowsAPI from 'dashboard/api/flows';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  flow: { type: Object, required: true },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const PAGE_SIZE = 25;

const executions = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const page = ref(1);
const hasMore = ref(false);
const expandedId = ref(null);
const steps = ref([]);
const stepsLoading = ref(false);

const fetchExecutions = async (targetPage = 1) => {
  try {
    const response = await FlowsAPI.getExecutions(props.flow.id, {
      page: targetPage,
    });
    const batch = response.data || [];
    executions.value =
      targetPage === 1 ? batch : [...executions.value, ...batch];
    hasMore.value = batch.length >= PAGE_SIZE;
    page.value = targetPage;
  } catch (error) {
    useAlert(t('FLOW_BUILDER.EXECUTIONS.LOAD_ERROR'));
  }
};

const loadExecutions = async () => {
  loading.value = true;
  await fetchExecutions(1);
  loading.value = false;
};

const loadMore = async () => {
  loadingMore.value = true;
  await fetchExecutions(page.value + 1);
  loadingMore.value = false;
};

const toggleSteps = async execution => {
  if (expandedId.value === execution.id) {
    expandedId.value = null;
    steps.value = [];
    return;
  }
  expandedId.value = execution.id;
  steps.value = [];
  stepsLoading.value = true;
  try {
    const response = await FlowsAPI.getExecutionSteps(
      props.flow.id,
      execution.id
    );
    steps.value = response.data.steps || [];
  } catch (error) {
    useAlert(t('FLOW_BUILDER.EXECUTIONS.STEPS_ERROR'));
  } finally {
    stepsLoading.value = false;
  }
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusBadgeClass = status => {
  switch (status) {
    case 'running':
      return 'bg-n-blue-3 text-n-blue-11';
    case 'completed':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'failed':
      return 'bg-n-ruby-3 text-n-ruby-11';
    default:
      return 'bg-n-slate-3 text-n-slate-11';
  }
};

const statusLabel = status =>
  t(`FLOW_BUILDER.EXECUTIONS.STATUS.${(status || '').toUpperCase()}`) || status;

const eventLabel = event =>
  t(`FLOW_BUILDER.EXECUTIONS.EVENTS.${(event || '').toUpperCase()}`) || event;

const waitingLabel = reason =>
  t(`FLOW_BUILDER.EXECUTIONS.WAITING.${(reason || '').toUpperCase()}`) ||
  reason;

const eventIcon = event => {
  switch (event) {
    case 'execution_started':
      return 'i-lucide-play';
    case 'message_sent':
      return 'i-lucide-send';
    case 'attachment_sent':
      return 'i-lucide-paperclip';
    case 'action_executed':
      return 'i-lucide-zap';
    case 'condition_evaluated':
      return 'i-lucide-git-branch';
    case 'scheduled':
      return 'i-lucide-clock';
    case 'deferred':
      return 'i-lucide-moon';
    case 'resumed':
      return 'i-lucide-play';
    case 'timeout':
      return 'i-lucide-timer-off';
    case 'input_received':
      return 'i-lucide-message-square';
    case 'completed':
      return 'i-lucide-check-circle-2';
    case 'failed':
      return 'i-lucide-x-circle';
    case 'watchdog_reenqueued':
      return 'i-lucide-refresh-ccw';
    case 'skipped':
      return 'i-lucide-skip-forward';
    default:
      return 'i-lucide-circle-dot';
  }
};

const eventIconClass = event => {
  switch (event) {
    case 'completed':
      return 'text-n-teal-9';
    case 'failed':
      return 'text-n-ruby-9';
    case 'scheduled':
    case 'deferred':
      return 'text-n-amber-9';
    case 'watchdog_reenqueued':
      return 'text-n-amber-11';
    default:
      return 'text-n-slate-9';
  }
};

const skipReasonLabel = reason =>
  t(`FLOW_BUILDER.EXECUTIONS.SKIP_REASONS.${(reason || '').toUpperCase()}`) ||
  reason;

const stepDetail = step => {
  const p = step.payload || {};
  if (p.error) return p.error;
  if (p.content) return p.content;
  if (p.action) return p.action;
  if (p.resume_at) return formatDate(p.resume_at);
  if (step.event === 'skipped' && p.reason) return skipReasonLabel(p.reason);
  if (p.reason)
    return waitingLabel(p.reason) !== p.reason
      ? waitingLabel(p.reason)
      : p.reason;
  return '';
};

onMounted(loadExecutions);
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-slate-1 rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[85vh]"
      >
        <div class="flex items-center justify-between p-6 pb-4">
          <div class="min-w-0">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('FLOW_BUILDER.EXECUTIONS.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-10 truncate">{{ flow.name }}</p>
          </div>
          <button
            class="p-1 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
            @click="emit('close')"
          >
            <span class="i-lucide-x h-5 w-5" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-6 pb-6">
          <div
            v-if="loading"
            class="flex items-center justify-center py-12 text-sm text-n-slate-10"
          >
            <span class="i-lucide-loader-2 w-5 h-5 animate-spin mr-2" />
            {{ t('FLOW_BUILDER.EXECUTIONS.LOADING') }}
          </div>

          <div
            v-else-if="!executions.length"
            class="flex items-center justify-center py-12 text-sm text-n-slate-10"
          >
            {{ t('FLOW_BUILDER.EXECUTIONS.EMPTY') }}
          </div>

          <div v-else class="flex flex-col gap-2">
            <div
              v-for="execution in executions"
              :key="execution.id"
              class="border border-n-slate-6 rounded-lg overflow-hidden"
            >
              <button
                class="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-n-slate-2 transition-colors"
                @click="toggleSteps(execution)"
              >
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                  :class="statusBadgeClass(execution.status)"
                >
                  {{ statusLabel(execution.status) }}
                </span>

                <span class="text-xs text-n-slate-11 flex-shrink-0">
                  {{ t('FLOW_BUILDER.EXECUTIONS.TABLE.CONVERSATION') }}
                  #{{ execution.conversation_id }}
                </span>

                <span class="text-xs text-n-slate-10 truncate">
                  {{ formatDate(execution.started_at) }}
                </span>

                <span
                  v-if="
                    execution.scheduled_resume_at &&
                    execution.status === 'running'
                  "
                  class="text-xs text-n-amber-11 truncate"
                  :title="waitingLabel(execution.waiting_reason)"
                >
                  {{ waitingLabel(execution.waiting_reason) }}
                  <span class="i-lucide-arrow-right w-3 h-3 inline-block" />
                  {{ formatDate(execution.scheduled_resume_at) }}
                </span>

                <span
                  v-if="execution.error_message"
                  class="text-xs text-n-ruby-9 truncate"
                >
                  {{ execution.error_message }}
                </span>

                <span class="flex-1" />

                <span class="text-xs text-n-slate-9 flex-shrink-0">
                  {{ execution.steps_count }}
                  <span
                    class="i-lucide-chevron-down w-4 h-4 inline-block align-middle transition-transform"
                    :class="{ 'rotate-180': expandedId === execution.id }"
                  />
                </span>
              </button>

              <div
                v-if="expandedId === execution.id"
                class="border-t border-n-slate-6 bg-n-slate-2 px-4 py-3"
              >
                <div
                  v-if="stepsLoading"
                  class="flex items-center gap-2 py-3 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-loader-2 w-4 h-4 animate-spin" />
                  {{ t('FLOW_BUILDER.EXECUTIONS.LOADING') }}
                </div>

                <ol v-else class="relative flex flex-col gap-2">
                  <li
                    v-for="step in steps"
                    :key="step.id"
                    class="flex items-start gap-2.5 text-xs"
                  >
                    <span
                      class="w-4 h-4 mt-0.5 flex-shrink-0"
                      :class="[
                        eventIcon(step.event),
                        eventIconClass(step.event),
                      ]"
                    />
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="font-medium text-n-slate-12">
                          {{ eventLabel(step.event) }}
                        </span>
                        <span
                          v-if="step.node_label || step.node_type"
                          class="text-n-slate-10"
                        >
                          {{ step.node_label || step.node_type }}
                        </span>
                        <span class="text-n-slate-9 ml-auto flex-shrink-0">
                          {{ formatDate(step.occurred_at) }}
                        </span>
                      </div>
                      <p
                        v-if="stepDetail(step)"
                        class="text-n-slate-10 truncate mt-0.5"
                      >
                        {{ stepDetail(step) }}
                      </p>
                    </div>
                  </li>
                  <li v-if="!steps.length" class="text-n-slate-10 py-2">
                    {{ t('FLOW_BUILDER.EXECUTIONS.EMPTY') }}
                  </li>
                </ol>
              </div>
            </div>

            <div v-if="hasMore" class="flex justify-center pt-2">
              <Button
                slate
                outline
                size="sm"
                :is-loading="loadingMore"
                :label="t('FLOW_BUILDER.EXECUTIONS.LOAD_MORE')"
                @click="loadMore"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
